import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Badge } from '@cmsgov/design-system'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { GovAndMenuWrapper } from '../GovAndMenuWrapper/GovAndMenuWrapper'

import { NavMenu } from '../NavMenu'
import { MobileNavigation } from '../MobileNavigation/MobileNavigation'

import User from '../../models/User'

import { getResourceNavItems, getAccountNav } from '../../data/navigation'

import { MessageCenterDropdown } from '../MessageCenterDropdown/MessageCenterDropdown'
import { ResourceNavDropdown } from '../ResourceNavDropdown/ResourceNavDropdown'
import { LiveChatMenu } from '../LiveChatMenu/LiveChatMenu'
import { DropdownMenu } from '../DropdownMenu/DropdownMenu'

import envIcon from '../../images/Icon_Envelope.svg'
import userIcon from '../../images/Icon_User.svg'
import logIn from '../../images/Log_In.svg'
import logOut from '../../images/Log_Out.svg'
import chatIcon from '../../images/Icon_Chat.svg'

import { properCase } from '../../helper/string'
import { accountUrls, messageUrls } from '../../data/urls'
import getSubdomain from '../../utils/getSubdomain'

import { CATEGORIES } from '../../constants/analytics.json'
import callEventHook from '../../utils/callEventHook'

import { checkSubsectionForActiveLinks } from '../../helper/navigationSubsections'
import { useSelector } from 'react-redux'
import { getUserLoggedInState } from '../../redux/reducers/users'
import { MobileResourceNavItem } from 'models/Navigation'
import { createAndEmitLogoutEvent } from 'utils/logoutEvents/create-and-emit-logout-event'

export interface GlobalHeaderProps {
  activeNavSection?: string;
  loginUrl: string;
  logoutUrl: string;
  user?: User;
  languageToggleLink: string;
  enableLiveChat?: boolean;
  disableLogin?: boolean;
  enableMessageCenter?: boolean;
  liveChatViewpage?: string;
  unreadCount?: number
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({ unreadCount, activeNavSection, enableMessageCenter = false, disableLogin = false, loginUrl, logoutUrl, user, languageToggleLink, enableLiveChat = false, liveChatViewpage }) => {
  const isLoggedIn = useSelector(getUserLoggedInState)

  const { t, i18n } = useTranslation('common')
  const lang = (i18n.language === 'es') ? 'es' : 'en'

  const langQueryParam = lang === 'es' ? '?lang=es-mx' : ''

  const flags = useFlags()
  const claimsNewLink: boolean = flags?.claimsNewLinkUrlsForClaims

  const legacyMessageCenterUrl = `https://${getSubdomain(process.env.REACT_APP_MY_MEDICARE_ENV || '')}medicare.gov/mbp/mymessages.aspx${langQueryParam}`
  const resourceNav = useMemo(
    () => (
      getResourceNavItems(lang, { flags }) ?? []
    ),
    [lang, flags]
  )

  const accountNav = useMemo(
    () => (
      getAccountNav(lang, { claimsNewLink, flags }) ?? []
    ),
    [lang, claimsNewLink, flags]
  )

  const firstName = user ? properCase(user.firstName) : ''

  const messageBadge = unreadCount && unreadCount > 0 ? <Badge className='ch-c-badge ch-c-badge__orange ch-c-navmenu__message-center__badge' variation='warn'>{unreadCount}</Badge> : <></>

  const resourceNavWithDropdowns = useMemo(() => resourceNav.map(section => {
    const filteredSubsections = section.subsections.filter(subsection => !(user && subsection.unauthedOnly))

    const isActive = activeNavSection ? section.navSection?.toLowerCase() === activeNavSection.toLowerCase() : checkSubsectionForActiveLinks(filteredSubsections)

    return {
      ...section,
      hasAccordionCaret: true,
      active: isActive,
      dropdown: (
        <ResourceNavDropdown name={section.label} sections={filteredSubsections} analyticsCategory={CATEGORIES.GENERAL_NAV} />
      )
    }
  }), [activeNavSection, resourceNav, user])

  const messageCenterButton = enableMessageCenter
    ? { className: 'ch-c-navmenu__message-center', locationUrls: messageUrls, icon: envIcon, label: t('Message Center'), badge: messageBadge, dropdown: (<MessageCenterDropdown />) }
    : { locationUrls: messageUrls, icon: envIcon, label: t('Message Center'), href: legacyMessageCenterUrl, target: '_blank' }

  const userMenu = []
  if (user) {
    const isActive = activeNavSection ? activeNavSection.toLowerCase() === 'account' : checkSubsectionForActiveLinks(accountNav)

    userMenu.push({
      active: isActive,
      locationUrls: accountUrls,
      gaText: 'Account',
      labelHasPotentialPii: true,
      icon: userIcon,
      label: firstName,
      dropdown: (
        <ResourceNavDropdown name='Account' analyticsCategory={CATEGORIES.PERSONAL_NAV} sections={accountNav} />
      )
    })
    userMenu.push(messageCenterButton)
  }

  if (enableLiveChat) {
    userMenu.push(
      {
        label: t('Chat'),
        icon: chatIcon,
        dropdown: <DropdownMenu><LiveChatMenu openChatAsPopup viewpage={liveChatViewpage} loginUrl={loginUrl} /></DropdownMenu>
      }
    )
  }

  if (!disableLogin) {
    if (user) {
      userMenu.push({
        label: t('Log out'),
        icon: logOut,
        href: logoutUrl,
        onClick: () => {
          createAndEmitLogoutEvent()
          callEventHook('onLogout')
        }
      })
    } else {
      userMenu.push({ label: t('Log in'), icon: logIn, href: loginUrl })
    }
  }

  // TODO: Update the standard nav to use the same navLists format as the MobileNavigation
  const mobileResourceNavItems: MobileResourceNavItem[] = resourceNav.map(link => {
    const subsections = link.subsections.filter((section: any) => !user || !section.unauthedOnly)
    return { ...link, subsections, analyticsCategory: CATEGORIES.GENERAL_NAV }
  })

  // id is only used to pass as a key when mapping over the links
  const mobileAccountNavItems: MobileResourceNavItem[] = useMemo(() => {
    const filterAuthItems = (item: { authOnly: boolean }) => !item.authOnly || isLoggedIn
    const filterEnabled = (item: {enabled: boolean}) => item.enabled

    return [
      {
        id: 'mobile-account-nav-1',
        analyticsCategory: CATEGORIES.PERSONAL_NAV,
        gaText: 'Account',
        labelHasPotentialPii: true,
        label: firstName,
        subsections: accountNav,
        authOnly: true,
        icon: userIcon,
        enabled: true
      },
      {
        id: 'mobile-account-nav-2',
        analyticsCategory: CATEGORIES.PERSONAL_NAV,
        label: t('Chat'),
        menu: <LiveChatMenu openChatAsPopup={false} viewpage={liveChatViewpage} loginUrl={loginUrl} />,
        authOnly: false,
        icon: chatIcon,
        enabled: enableLiveChat
      },
      {
        id: 'mobile-account-nav-3',
        analyticsCategory: CATEGORIES.PERSONAL_NAV,
        label: t('Message Center'),
        href: legacyMessageCenterUrl,
        authOnly: true,
        icon: envIcon,
        enabled: true,
        isMessageCenter: true
      }
    ]
      .filter(filterAuthItems)
      .filter(filterEnabled)
      .map(({ authOnly, enabled, ...menuItem }) => menuItem)
  }
  ,
  [enableLiveChat, firstName, accountNav, isLoggedIn, t, liveChatViewpage, legacyMessageCenterUrl, loginUrl])

  return (
    <GovAndMenuWrapper languageToggleLink={languageToggleLink} isLoggedIn={isLoggedIn}>
      <MobileNavigation
        navLists={[mobileResourceNavItems, mobileAccountNavItems]}
        disableLogin={disableLogin}
        languageToggleLink={languageToggleLink}
        loginUrl={loginUrl}
        logoutUrl={logoutUrl}
        enableMessageCenter={enableMessageCenter}
      />
      {/* This needs to be the last menu on the page. Styles in .ch-c-navmenu depends on order */}
      <NavMenu className='ch-u-remove--md' items={resourceNavWithDropdowns} ariaLabel='Main' analyticsCategory={CATEGORIES.GENERAL_NAV} />
      {userMenu.length > 0 && <NavMenu className='ch-u-remove--md' items={userMenu} ariaLabel='Personal' analyticsCategory={CATEGORIES.PERSONAL_NAV} />}
    </GovAndMenuWrapper>
  )
}
